<template>
  <div
    v-if="!isWaitingForAnswer"
    class="content-container"
  >
    <default-overlay />
    <media-text
      v-if="kbAnswerLocal.value"
      :data="kbAnswerLocal"
      class="text media-text"
    />
    <div
      v-if="!currentContent.attrs?.invisibleButtons"
      :class="{
        'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      }"
      class="buttons-container"
    >
      <div
        v-for="({ button, next }, idx) in buttons"
        :key="idx"
        :style="idx === selectedButton ? buttonActiveStyle : buttonInactiveStyles"
        :class="{ 'no-avatar': isHideAvatar && !isKeepAvatarDesign }"
        class="button"
        @click="interruptAndRun(idx, next)"
      >
        <div
          :dir="languageDir"
          class="button-text"
        >
          {{ button.text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useStt, useTts, ShortCodeService, removeEmojisFromString } from '@ht/lib-plural-vue';
import { rateChat } from '@/api';
import useSequence from '@/composables/useSequence';
import useContent from '@/composables/useContent';
import useConfig from '@/composables/useConfig';
import useChatbot from '@/composables/useChatbot';
import useDynamicButtons from '@/composables/useDynamicButtons';
import useKnowledgeBase from '@/composables/useKnowledgeBase';
import useAnalytics from '@/composables/useAnalytics';
import DefaultOverlay from '@/components/DefaultOverlay';
import MediaText from '@/components/MediaText';
import { SYSTEM_VARIABLES_MAP, INTERACTION_DELAY } from '@/js/constants';
import Say from '@/actions/say';
import FontLoader from '@/js/fontLoader';

const BUTTONS_KEY_ORDER = ['positive', 'negative', 'neutral'];
const TEXT_STREAM_DELAY = 10;
export default defineComponent({
  name: 'KnowledgeBaseUIComponent',

  emits: ['processing'],

  components: {
    DefaultOverlay,
    MediaText,
  },

  setup(props, { emit }) {
    const { runSequence, interruptSequence, currentSeq } = useSequence();
    const { currentContent, isPaused, isHideAvatar, isKeepAvatarDesign } = useContent();
    const { recognizedText, sttStart, sttStop, sttPhrases } = useStt();
    const { preFetchAudioContent } = useTts();
    const { askChatbot } = useChatbot();
    const {
      getVariable,
      currentLanguage,
      initialConfig,
      currentConfig,
      languageDir,
      isPreview,
      setFlowStateBackToStart,
      setVariables,
    } = useConfig();
    const { amendAction, lastAnalyticData } = useAnalytics();
    const { askQuestion } = useKnowledgeBase();

    let sayAction;
    let conversationIdLocal;
    let shouldComplete;
    const isReady = ref(false);
    const isWaitingForAnswer = ref(true);
    const fullText = ref('');
    const humanRepliedPath = computed(() => currentContent.value?.attrs?.humanReplied);
    const knowledgeBaseId = computed(() => {
      const attrs = currentContent.value?.attrs;
      if (!attrs) return null;
      if (attrs.knowledgeBaseId) {
        return attrs.knowledgeBaseId;
      }
      if (attrs.knowledgeBaseVariableId) {
        const variable = getVariable(attrs.knowledgeBaseVariableId);
        return variable?.value;
      }
      return null;
    });
    const variableId = computed(() => currentContent.value?.attrs?.variableId);

    const formattedButtons = computed(() => {
      if (!currentContent.value?.attrs?.humanReplyOptions) {
        return [];
      }

      return BUTTONS_KEY_ORDER
        .reduce((res, key) => {
          const { text, variations } = currentContent.value.attrs.humanReplyOptions[key];
          let rate = 0;
          if (key === 'positive') {
            rate = 1;
          } else if (key === 'negative') {
            rate = -1;
          }
          res.push({
            button: { id: key, text, variations, rate },
            next: humanRepliedPath.value,
          });
          return res;
        }, []);
    });
    const buttons = useDynamicButtons(formattedButtons.value);
    const selectedButton = ref(-1);
    const buttonInactiveStyles = computed(() => ({
      backgroundColor: currentContent.value.styles?.button_inactive_background_color,
      color: currentContent.value.styles?.button_inactive_text_color,
      fontFamily: FontLoader.buildFontFamily(currentContent.value.styles?.answers_font),
      '--font-size': `${currentContent.value.styles?.answers_fontSize ?? 28}px`,
    }));
    const buttonActiveStyle = computed(() => ({
      backgroundColor: currentContent.value.styles?.button_active_background_color,
      color: currentContent.value.styles?.button_active_text_color,
      fontFamily: FontLoader.buildFontFamily(currentContent.value.styles?.answers_font),
      '--font-size': `${currentContent.value.styles?.answers_fontSize ?? 28}px`,
    }));
    const kbAnswerLocal = computed(() => ({
      ...currentContent.value?.attrs?.text,
      fontSize: currentContent.value?.attrs?.text?.fontSize ?? 36,
      value: fullText.value,
    }));

    let isUnmounted = false;
    const wordsQueue = [];
    const speech = {
      ready: false,
      promise: null,
      sentence: '',
    };
    let streamIntervalId = null;
    let streamIndex = 0;

    function interruptAndRun(index, id) {
      if (selectedButton.value !== -1 || !isReady.value) {
        return;
      }

      selectedButton.value = index;
      amendAction({
        questionButtonSelected: buttons.value[index].button.text,
      });

      setTimeout(() => {
        if (id) {
          if (!isPaused.value) {
            interruptSequence();
            setFlowStateBackToStart(id);
            runSequence(id);
          } else {
            shouldComplete = {
              next: id,
            };
          }
        } else {
          selectedButton.value = -1;
        }
      }, INTERACTION_DELAY); // nextTick will not wait for rerender of selected button

      rateChat({
        knowledgeBaseId: knowledgeBaseId.value,
        rating: buttons.value[index].button.rate,
        conversationId: conversationIdLocal,
        sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
      });
    }

    function handleError() {
      emit('processing', false);
      const errorPath = currentContent.value?.attrs?.knowledgeBaseError;

      if (!isPaused.value) {
        interruptSequence(); // stop the no interaction timer
        if (errorPath) {
          setFlowStateBackToStart(errorPath);
          runSequence(errorPath);
        }
      } else {
        shouldComplete = {
          next: errorPath,
        };
      }
    }

    async function sayText() {
      sayAction = new Say({ utterance: speech.sentence, forceReuse: true });
      await sayAction.execute();
      speech.ready = false;

      if (isUnmounted) {
        return;
      }

      sayAction = null;
      sttStart({
        sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
        elementId: lastAnalyticData.value.elementId,
      });

      if (currentContent.value.attrs.noAnswer && !isUnmounted) {
        setFlowStateBackToStart(currentContent.value.attrs.noAnswer);
        runSequence(currentContent.value.attrs.noAnswer);
      }
    }

    async function startStreaming() {
      // wait for the promise to be sure that text and speech will start at the same time
      await speech.promise;
      isWaitingForAnswer.value = false;
      emit('processing', false);

      if (!isPaused.value) {
        sayText();
      }

      streamIntervalId = setInterval(() => {
        if (streamIndex < wordsQueue.length) {
          fullText.value += wordsQueue[streamIndex];
          streamIndex += 1;
        } else {
          clearInterval(streamIntervalId);
        }
      }, TEXT_STREAM_DELAY);
    }

    async function askQuestionHandler() {
      if (!variableId.value && !currentContent.value.attrs?.customPrompt) {
        return;
      }

      const question = getVariable(variableId.value)?.value
          || new ShortCodeService(currentContent.value.attrs?.customPrompt || '').matchAttributes().getValue();

      if (!question || !knowledgeBaseId.value) {
        return;
      }

      emit('processing', true);

      speech.ready = false;
      speech.promise = null;
      speech.sentence = '';
      fullText.value = '';
      wordsQueue.splice(0, wordsQueue.length);
      streamIndex = 0;
      if (streamIntervalId) {
        clearInterval(streamIntervalId);
      }

      const { error, text, conversationId, postMessage } = await askQuestion({
        question,
        knowledgeBaseId: knowledgeBaseId.value,
      });

      if (isUnmounted) {
        return;
      }

      if (error) {
        handleError();
        return;
      }

      let combinedText = text;
      if (postMessage) {
        const postMessageParsed = new ShortCodeService(`\n${postMessage}`)
          .matchAttributes()
          .getValue();

        combinedText = `${text} ${postMessageParsed}`;
      }

      wordsQueue.push(...combinedText.match(/.{1,3}/gs));

      speech.sentence = combinedText;
      speech.promise = preFetchAudioContent({
        utterance: combinedText,
        currentLanguage,
        config: initialConfig.value,
        meta: {
          projectId: currentConfig.value.projectId,
          sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
          isPreview: isPreview(),
          elementId: lastAnalyticData.value.elementId,
        },
      }); // prefetch audio before start streaming of text
      speech.ready = true;

      startStreaming();

      conversationIdLocal = conversationId;
      isReady.value = true;
    }

    watch(recognizedText, async (value) => {
      const recText = value.text.trim();
      if (recText && selectedButton.value === -1 && value.isFinal) {
        const index = buttons.value.findIndex(
          ({ button }) => button.variations.some(
            (v) => recText.toLowerCase().includes(
              removeEmojisFromString(v).toLowerCase().replace('\n', ' '),
            ),
          ),
        );
        if (index !== -1) {
          const button = buttons.value[index];
          if (button.next) {
            interruptAndRun(index, button.next);
          }
        } else if (currentContent.value.attrs.usePluralDialogs) {
          await askChatbot(recText);
        } else if (currentContent.value.attrs.anythingElse) {
          const next = currentContent.value.attrs.anythingElse;
          if (!isPaused.value) {
            interruptSequence();
            setFlowStateBackToStart(next);
            runSequence(next);
          } else {
            shouldComplete = {
              next,
            };
          }
        }
      }
    });

    onMounted(async () => {
      setVariables([{
        id: SYSTEM_VARIABLES_MAP.CURRENT_DESIGN_ELEMENT.id,
        value: { value: currentSeq.value.id },
      }]);
      await askQuestionHandler();

      if (isReady.value && !isUnmounted) {
        sttPhrases(
          BUTTONS_KEY_ORDER.map((key, index) => ({
            variations: buttons.value[index].button.variations,
          })),
        );
      }
    });

    onBeforeUnmount(() => {
      emit('processing', false);
      isUnmounted = true;
      sttStop();
      if (sayAction) {
        sayAction.stop();
      }
    });

    watch(isPaused, () => {
      if (isPaused.value) {
        sayAction?.pause();
      } else if (shouldComplete) {
        interruptSequence();
        if (shouldComplete.next) {
          setFlowStateBackToStart(shouldComplete.next);
          runSequence(shouldComplete.next);
        }
      } else if (sayAction) {
        sayAction.resume();
      } else if (!isWaitingForAnswer.value && speech.ready) {
        sayText();
      }
    });

    return {
      isWaitingForAnswer,
      selectedButton,
      currentContent,
      kbAnswerLocal,
      buttons,
      buttonInactiveStyles,
      buttonActiveStyle,
      interruptAndRun,
      languageDir,
      isHideAvatar,
      isKeepAvatarDesign,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "sass:math";
@import "@/scss/common-ui.scss";

.content-container {
  @extend .content-container;
}

.buttons-container {
  @extend .buttons-container;
}

.button {
  @extend .button;
}

.button-text {
  @extend .button-text;
}
</style>
